// src/components/riskassessment/StrategyRecommendations.js
//
// Shows the user's recommended ARCrypto vaults from the 7-vault inventory.
// Source of truth for vault stats: ARCrypto_Risk_Assessment_Blueprint.md
//
// Props:
//   riskScore         — composite 0–100 score (used to derive profile if missing)
//   vaultAllocation   — { leoBTC, leoETH, gemBTC, gemini, scorpioV, gemETH, gemSOL, cash }
//   profileType       — "Ultra-Conservative" | "Moderate" | "Growth-Oriented" | "Aggressive" | "Ultra-Aggressive"
//   safetyOverrides   — array of { rule, message, capTo } from scoring engine
//   className

import React from "react";
import { Shield, Target, AlertTriangle, ExternalLink } from "lucide-react";

/* ─── Vault catalog — from blueprint section 1 ────────────────────────── */
const VAULTS = [
  {
    key: "leoBTC",
    name: "Leo BTC Bull Run",
    risk: 3,
    type: "Long only",
    asset: "BTC",
    leverage: "None",
    maxDD: -7.43,
    keyMetric: "181 days in cash",
    behavior: "Sits in cash, only deploys on confirmed trends",
    bestFor: "Capital preservation, patient long-term investors",
    color: "#22c55e",
  },
  {
    key: "leoETH",
    name: "Leo ETH",
    risk: 4,
    type: "Long only",
    asset: "ETH",
    leverage: "None",
    maxDD: -20.69,
    keyMetric: "+9,484% backtest",
    behavior: "ETH momentum, 100% capital on trend",
    bestFor: "ETH bulls who want algorithmic timing + capital preservation",
    color: "#10b981",
  },
  {
    key: "gemBTC",
    name: "Gemini BTC",
    risk: 5,
    type: "Long/short",
    asset: "BTC",
    leverage: "None",
    maxDD: -15.79,
    keyMetric: "28/28 recoveries",
    behavior: "Frequent trading with controlled losses",
    bestFor: "Balanced growth with controlled risk",
    color: "#3b82f6",
  },
  {
    key: "gemini",
    name: "Gemini",
    risk: 5,
    type: "Long/short",
    asset: "BTC/ETH/SOL",
    leverage: "Derivatives",
    maxDD: -17.6,
    keyMetric: "Sharpe 2.60",
    behavior: "33/33/33 split, diversified across three assets",
    bestFor: "Diversified exposure with smoothed drawdowns",
    color: "#6366f1",
  },
  {
    key: "scorpioV",
    name: "Scorpio V",
    risk: 7,
    type: "Long/short",
    asset: "BTC",
    leverage: "Up to 12x",
    maxDD: -20.44,
    keyMetric: "+786% live",
    behavior: "High-conviction leveraged BTC trends",
    bestFor: "High-growth seekers with strong conviction and long horizon",
    color: "#f59e0b",
  },
  {
    key: "gemETH",
    name: "Gemini ETH",
    risk: 8,
    type: "Long/short",
    asset: "ETH",
    leverage: "None",
    maxDD: -37.5,
    keyMetric: "+3,290% backtest",
    behavior: "Highest returns + deepest drawdowns in the suite",
    bestFor: "Maximum return seekers with high tolerance for deep drawdowns",
    color: "#ef4444",
  },
  {
    key: "gemSOL",
    name: "Gemini SOL",
    risk: 8,
    type: "Long/short directional",
    asset: "SOL",
    leverage: "None",
    maxDD: -49,
    keyMetric: "+81,400% backtest",
    behavior: "Directional SOL — struggles in chop, shines in trends",
    bestFor: "Bearish SOL outlook or high-volatility conviction",
    color: "#dc2626",
  },
];

/* ─── Profile defaults if vaultAllocation isn't passed ────────────────── */
const ALLOCATION_BY_PROFILE = {
  "Ultra-Conservative": { leoBTC: 35, leoETH: 25, gemBTC: 15, gemini: 5, scorpioV: 0, gemETH: 0, gemSOL: 0, cash: 20 },
  Moderate:             { leoBTC: 20, leoETH: 15, gemBTC: 20, gemini: 15, scorpioV: 10, gemETH: 0, gemSOL: 0, cash: 20 },
  "Growth-Oriented":    { leoBTC: 10, leoETH: 10, gemBTC: 15, gemini: 15, scorpioV: 20, gemETH: 10, gemSOL: 5, cash: 15 },
  Aggressive:           { leoBTC: 5, leoETH: 5, gemBTC: 10, gemini: 10, scorpioV: 25, gemETH: 20, gemSOL: 15, cash: 10 },
  "Ultra-Aggressive":   { leoBTC: 0, leoETH: 5, gemBTC: 5, gemini: 10, scorpioV: 20, gemETH: 25, gemSOL: 25, cash: 10 },
};

const scoreToProfile = (score) => {
  if (score <= 20) return "Ultra-Conservative";
  if (score <= 40) return "Moderate";
  if (score <= 60) return "Growth-Oriented";
  if (score <= 80) return "Aggressive";
  return "Ultra-Aggressive";
};

const riskBadgeColor = (risk) => {
  if (risk <= 4) return "text-arc-light bg-emerald-500/10 border-emerald-500/30";
  if (risk <= 6) return "text-arc-gold bg-arc-gold/10 border-arc-gold/30";
  return "text-arc-light bg-red-500/10 border-red-500/30";
};

const StrategyRecommendations = ({
  riskScore = 0,
  vaultAllocation,
  profileType,
  safetyOverrides = [],
  className = "",
}) => {
  // Resolve profile + allocation from whatever the caller passed
  const profile = profileType || scoreToProfile(riskScore);
  const allocation = vaultAllocation || ALLOCATION_BY_PROFILE[profile] || ALLOCATION_BY_PROFILE.Moderate;

  // Recommended = vaults with > 0 allocation. Sort by allocation desc.
  const recommended = VAULTS
    .map((v) => ({ ...v, alloc: allocation[v.key] || 0 }))
    .filter((v) => v.alloc > 0)
    .sort((a, b) => b.alloc - a.alloc);

  // Excluded = vaults with 0 allocation in this profile
  const excluded = VAULTS
    .map((v) => ({ ...v, alloc: allocation[v.key] || 0 }))
    .filter((v) => v.alloc === 0);

  return (
    <div className={`surface overflow-hidden ${className}`}>
      {/* Header */}
      <div className="mesh p-6 border-b border-arc-line">
        <div className="flex items-center gap-3 mb-2">
          <Target className="w-5 h-5 text-arc-gold" strokeWidth={1.75} />
          <p className="eyebrow">Profile · {profile}</p>
        </div>
        <h3 className="headline text-2xl">Recommended for Your Risk Level</h3>
        <p className="text-arc-muted text-sm mt-2 max-w-2xl">
          Based on your composite score of {Math.round(riskScore)}/100, here are the
          ARCrypto Managed Vaults included in your allocation. All percentages
          sum to 100% (including the cash buffer).
        </p>
      </div>

      {/* Safety override notice */}
      {safetyOverrides && safetyOverrides.length > 0 && (
        <div className="mx-6 mt-6 rounded-2xl border border-arc-deep/40 bg-arc-deep/10 p-4">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={16} className="text-arc-light flex-shrink-0 mt-0.5" />
            <div>
              <p className="eyebrow mb-1.5 text-arc-light">Capacity override active</p>
              <p className="text-sm text-arc-muted">
                Your allocation has been adjusted to protect your financial stability:
              </p>
              <ul className="mt-2 space-y-1 text-sm text-arc-muted">
                {safetyOverrides.map((o, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-arc-light">•</span>
                    <span>{o.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Recommended vaults grid */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {recommended.map((v) => (
            <div
              key={v.key}
              className="rounded-2xl border border-arc-line bg-white/[0.02] p-5"
              style={{ borderLeft: `3px solid ${v.color}` }}
            >
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold text-arc-ink text-lg">{v.name}</h4>
                <span
                  className={`text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border ${riskBadgeColor(
                    v.risk
                  )}`}
                >
                  Risk {v.risk}/10
                </span>
              </div>

              <div className="flex items-baseline gap-2 mb-3">
                <span className="num text-2xl text-arc-gold">{v.alloc}%</span>
                <span className="text-xs text-arc-dim">of portfolio</span>
              </div>

              <div className="space-y-1 text-xs text-arc-muted">
                <div className="flex justify-between">
                  <span>Asset</span>
                  <span className="text-arc-ink num">{v.asset}</span>
                </div>
                <div className="flex justify-between">
                  <span>Type</span>
                  <span className="text-arc-ink">{v.type}</span>
                </div>
                <div className="flex justify-between">
                  <span>Leverage</span>
                  <span className="text-arc-ink num">{v.leverage}</span>
                </div>
                <div className="flex justify-between">
                  <span>Max DD</span>
                  <span className="num text-red-400">{v.maxDD}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Track</span>
                  <span className="text-arc-light num">{v.keyMetric}</span>
                </div>
              </div>

              <p className="text-xs text-arc-dim italic mt-3 leading-relaxed">
                {v.behavior}
              </p>
            </div>
          ))}

          {/* Cash buffer card */}
          {allocation.cash > 0 && (
            <div
              className="rounded-2xl border border-arc-line bg-white/[0.02] p-5"
              style={{ borderLeft: `3px solid #a1a1aa` }}
            >
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold text-arc-ink text-lg">Cash / Stables</h4>
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-arc-line text-arc-muted">
                  Buffer
                </span>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="num text-2xl text-arc-gold">{allocation.cash}%</span>
                <span className="text-xs text-arc-dim">of portfolio</span>
              </div>
              <p className="text-xs text-arc-dim italic mt-3 leading-relaxed">
                Provides psychological safety and rebalancing optionality. Even
                Ultra-Aggressive profiles keep at least 10% liquid.
              </p>
            </div>
          )}
        </div>

        {/* Excluded vaults section */}
        {excluded.length > 0 && (
          <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-5">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={14} className="text-arc-dim" />
              <p className="eyebrow">Excluded from your allocation</p>
            </div>
            <p className="text-xs text-arc-muted mb-3 leading-relaxed">
              These vaults are not part of your recommended mix at the
              <strong className="text-arc-ink"> {profile}</strong> profile —
              their drawdown profiles exceed what your risk capacity supports.
            </p>
            <div className="flex flex-wrap gap-2">
              {excluded.map((v) => (
                <span
                  key={v.key}
                  className="text-xs px-2.5 py-1 rounded-full border border-arc-line text-arc-dim flex items-center gap-1.5"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: v.color, opacity: 0.5 }}
                  />
                  {v.name} <span className="text-arc-dim/60">· {v.risk}/10</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="px-6 pb-6">
        <a
          href="https://app.enzyme.finance"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-tactile btn-outline w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm"
        >
          View vaults on Enzyme.finance
          <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );
};

export default StrategyRecommendations;
